import { useEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useMessageInstanceEvent } from '../index';
import { usePlacementContext } from '../placement-context';
import type { RecommendationContext } from '../recommendation-context';
import type { DroppedMessageIdentity } from '../send-message-instance-dropped';
import { postNarrowExposureDrop } from './drop-event';
import { evaluateCohort } from './evaluate-cohort';
import type { NarrowExposureConfig, NarrowExposureDecision } from './types';

export type NarrowExposureMessage = Omit<DroppedMessageIdentity, 'recommendation'> & {
	messageInstanceId: string;
	inAppNarrowExposureExperiment?: NarrowExposureConfig | null;
	recommendationSession?: RecommendationContext;
};

export type CustomNarrowExposureExperimentConfigResult =
	| NarrowExposureConfig
	| { skipEvaluation: 'showMessage' }
	| { skipEvaluation: 'dropMessage'; config: NarrowExposureConfig };

export type MessagesWithExptAndChoreoDisabledOptions<
	T extends NarrowExposureMessage = NarrowExposureMessage,
> = {
	/** Placement ID override when selecting outside a placement provider. */
	placementId?: string;
	/**
	 * Display cap, independent of backend maxItems/targetItems. Omit to consider all candidates;
	 * zero selects nothing. Candidates after the cap are neither resolved nor evaluated.
	 */
	maxMessagesToDisplay?: number;
	/**
	 * Use only for custom in-app message experiments that the static
	 * `inAppNarrowExposureExperiment` config cannot express.
	 *
	 * Resolvers are keyed by message template ID and called for each matching candidate reached
	 * before the display cap. Templates without a resolver keep their backend config.
	 *
	 * - Config: replaces the backend config on the selected message.
	 * - `{ skipEvaluation: 'showMessage' }`: clears the experiment and allows normal rendering.
	 * - `{ skipEvaluation: 'dropMessage', config }`: excludes the candidate without exposure.
	 *   After commit, posts a `narrowExposureExperiment` drop with cohort `unevaluated`;
	 *   `config` supplies the experiment metadata.
	 *
	 * Call hooks in the placement and capture their results in the resolvers.
	 * Resolvers only select configs, synchronously and without side effects; this hook
	 * handles Statsig evaluation and exposure.
	 * Requires post-office_enable_frontend-experiment-resolver. With the gate off, resolvers are
	 * ignored; placements must withhold messages that depend on them.
	 *
	 * @example
	 * useMessagesWithExptAndChoreoDisabled(messages, {
	 *   customNarrowExposureExperimentConfigResolvers: {
	 *     'custom-message': message => {
	 *       if (message.experimentCohort === 'new-users') return newUsersConfig;
	 *       if (message.experimentCohort === 'existing-users') return existingUsersConfig;
	 *       return { skipEvaluation: 'dropMessage', config: existingUsersConfig };
	 *     },
	 *   },
	 * });
	 */
	customNarrowExposureExperimentConfigResolvers?: Partial<
		Record<string, (message: T) => CustomNarrowExposureExperimentConfigResult>
	>;
};

/**
 * For placements with choreography disabled. Call after eligibility/dismissal filtering,
 * then render the returned list without further slicing.
 * Evaluated candidates use normal Statsig exposure logging. Drop events fire after commit
 * and are deduplicated by the shared drop helper. This hook never emits viewed events.
 */
export const useMessagesWithExptAndChoreoDisabled = <T extends NarrowExposureMessage>(
	messages: readonly T[],
	options: MessagesWithExptAndChoreoDisabledOptions<T> = {},
): readonly T[] => {
	const placement = usePlacementContext();
	const placementId = options.placementId ?? placement.placementId;
	const { messageInstanceDropped } = useMessageInstanceEvent();
	const customNarrowExposureExperimentConfigResolvers =
		options.customNarrowExposureExperimentConfigResolvers &&
		fg('post-office_enable_frontend-experiment-resolver')
			? options.customNarrowExposureExperimentConfigResolvers
			: undefined;
	const configuredCap = options.maxMessagesToDisplay;
	const cap = configuredCap === undefined ? Infinity : Math.max(0, Math.floor(configuredCap) || 0);
	const selected: T[] = [];
	const droppedMessages: {
		message: T;
		config: NarrowExposureConfig;
		decision: Exclude<NarrowExposureDecision, { outcome: 'show' }>;
	}[] = [];

	for (const message of messages) {
		if (selected.length >= cap) break;
		const resolveCustomNarrowExposureExperimentConfig =
			customNarrowExposureExperimentConfigResolvers &&
			message.messageTemplateId !== undefined &&
			Object.prototype.hasOwnProperty.call(
				customNarrowExposureExperimentConfigResolvers,
				message.messageTemplateId,
			)
				? customNarrowExposureExperimentConfigResolvers[message.messageTemplateId]
				: undefined;
		const resolution = resolveCustomNarrowExposureExperimentConfig
			? resolveCustomNarrowExposureExperimentConfig(message)
			: message.inAppNarrowExposureExperiment;
		if (resolution && 'skipEvaluation' in resolution) {
			if (resolution.skipEvaluation === 'dropMessage') {
				droppedMessages.push({
					message,
					config: resolution.config,
					decision: { outcome: 'unevaluated' },
				});
			} else {
				selected.push({ ...message, inAppNarrowExposureExperiment: undefined });
			}
			continue;
		}
		const config = resolution;
		const resolvedMessage = resolveCustomNarrowExposureExperimentConfig
			? { ...message, inAppNarrowExposureExperiment: config }
			: message;
		if (!config) {
			selected.push(resolvedMessage);
			continue;
		}
		const decision = evaluateCohort(config);
		if (decision.outcome === 'show') {
			selected.push(resolvedMessage);
		} else {
			droppedMessages.push({ message, config, decision });
		}
	}

	// Cohorts can change without the list changing, so check each committed render.
	useEffect(() => {
		for (const { message, config, decision } of droppedMessages) {
			postNarrowExposureDrop(
				(dropDetails) =>
					messageInstanceDropped(dropDetails, {
						...message,
						placementId,
						recommendation: message.recommendationSession ?? {},
					}),
				message.messageInstanceId,
				config,
				decision,
			);
		}
	});

	return selected;
};
